/**
 * React Query hooks for filter rules
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import createDebug from 'debug';
import {
	createFilterRule,
	type CreateFilterRuleInput,
	deleteFilterRule,
	duplicateFilterRule,
	type FilterRule,
	listFilterRules,
	reorderFilterRules,
	runFilterRule,
	testFilterConditions,
	toggleFilterRule,
	updateFilterRule,
	type UpdateFilterRuleInput,
	type Condition,
} from '@/api/filters';

const debug = createDebug('app:hooks:filters');

export function useFilterRules() {
	return useQuery({
		queryKey: ['filterRules'],
		queryFn: async () => {
			const { rules } = await listFilterRules();
			return rules;
		},
		staleTime: 30000,
	});
}

export function useCreateFilterRule() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: CreateFilterRuleInput) => createFilterRule(input),
		onSuccess: ({ rule }) => {
			debug('createFilterRule success: id=%d name=%s', rule.id, rule.name);
			queryClient.invalidateQueries({ queryKey: ['filterRules'] });
		},
		onError: (error) => {
			debug('createFilterRule error: %o', error);
		},
	});
}

export function useUpdateFilterRule() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: UpdateFilterRuleInput }) =>
			updateFilterRule(id, data),
		onSuccess: ({ rule }) => {
			debug('updateFilterRule success: id=%d', rule.id);
			queryClient.invalidateQueries({ queryKey: ['filterRules'] });
		},
		onError: (error) => {
			debug('updateFilterRule error: %o', error);
		},
	});
}

export function useDeleteFilterRule() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: number) => deleteFilterRule(id),
		onSuccess: (_data, id) => {
			debug('deleteFilterRule success: id=%d', id);
			queryClient.invalidateQueries({ queryKey: ['filterRules'] });
		},
		onError: (error) => {
			debug('deleteFilterRule error: %o', error);
		},
	});
}

export function useToggleFilterRule() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: number) => toggleFilterRule(id),
		onMutate: async (id) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({ queryKey: ['filterRules'] });

			// Snapshot current value
			const previousRules = queryClient.getQueryData<FilterRule[]>(['filterRules']);

			// Optimistically toggle
			queryClient.setQueryData<FilterRule[]>(['filterRules'], (old) => {
				if (!old) return old;
				return old.map((rule) =>
					rule.id === id ? { ...rule, enabled: !rule.enabled } : rule
				);
			});

			return { previousRules };
		},
		onError: (error, _id, context) => {
			debug('toggleFilterRule error: %o', error);
			if (context?.previousRules) {
				queryClient.setQueryData(['filterRules'], context.previousRules);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ['filterRules'] });
		},
	});
}

export function useDuplicateFilterRule() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, newName }: { id: number; newName?: string }) =>
			duplicateFilterRule(id, newName),
		onSuccess: ({ rule }) => {
			debug('duplicateFilterRule success: id=%d name=%s', rule.id, rule.name);
			queryClient.invalidateQueries({ queryKey: ['filterRules'] });
		},
		onError: (error) => {
			debug('duplicateFilterRule error: %o', error);
		},
	});
}

export function useReorderFilterRules() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (orderedIds: number[]) => reorderFilterRules(orderedIds),
		onMutate: async (orderedIds) => {
			await queryClient.cancelQueries({ queryKey: ['filterRules'] });
			const previousRules = queryClient.getQueryData<FilterRule[]>(['filterRules']);

			// Optimistically reorder
			queryClient.setQueryData<FilterRule[]>(['filterRules'], (old) => {
				if (!old) return old;
				const ruleMap = new Map(old.map((r) => [r.id, r]));
				return orderedIds
					.map((id, index) => {
						const rule = ruleMap.get(id);
						return rule ? { ...rule, priority: index } : null;
					})
					.filter((r): r is FilterRule => r !== null);
			});

			return { previousRules };
		},
		onError: (error, _ids, context) => {
			debug('reorderFilterRules error: %o', error);
			if (context?.previousRules) {
				queryClient.setQueryData(['filterRules'], context.previousRules);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ['filterRules'] });
		},
	});
}

export function useRunFilterRule() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			options,
		}: {
			id: number;
			options?: { linkIds?: number[]; dryRun?: boolean; limit?: number };
		}) => runFilterRule(id, options),
		onSuccess: (result) => {
			debug(
				'runFilterRule success: rule=%d matched=%d/%d dryRun=%s',
				result.rule.id,
				result.matchedLinks,
				result.totalLinks,
				result.dryRun
			);
			// If not a dry run, refresh links as they may have changed
			if (!result.dryRun) {
				queryClient.invalidateQueries({ queryKey: ['links'] });
			}
		},
		onError: (error) => {
			debug('runFilterRule error: %o', error);
		},
	});
}

export function useTestFilterConditions() {
	return useMutation({
		mutationFn: ({
			conditions,
			options,
		}: {
			conditions: Condition[];
			options?: { linkIds?: number[]; limit?: number };
		}) => testFilterConditions(conditions, options),
		onSuccess: (result) => {
			debug('testFilterConditions success: matched=%d/%d', result.matched, result.total);
		},
		onError: (error) => {
			debug('testFilterConditions error: %o', error);
		},
	});
}
